import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Clock,
  Calendar,
  BookOpen,
  Play,
  FileText,
  Music,
  Video,
  Folder,
  ArrowLeft,
  Home,
  ChevronRight,
} from "lucide-react";
import api from "../utils/api";
import MediaViewer from "../components/MediaViewer";
import AudioViewer from "../components/AudioViewer";
import VideoViewer from "../components/VideoViewer";
import LectionaryCalendar from "../components/LectionaryCalendar";
import ReadingsModal from "../components/ReadingsModal";
import URLBasedAudioPlayer from "../components/URLBasedAudioPlayer";
import Linkify from "linkify-react";
import { getToneInfo } from "../utils/liturgicalTones";

const Services = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [regularServices, setRegularServices] = useState([]);
  const [specialServices, setSpecialServices] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  // Resources state
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [activeResourceFilter, setActiveResourceFilter] = useState("All");
  const [selectedResource, setSelectedResource] = useState(null);
  const [showResourceModal, setShowResourceModal] = useState(false);

  // Lectionary state
  const [lectionaryEvents, setLectionaryEvents] = useState([]);
  const [lectionaryCache, setLectionaryCache] = useState({}); // Cache by year
  const [lectionaryLoading, setLectionaryLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const currentYear = new Date().getFullYear(); // Fixed to current year
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showReadingsModal, setShowReadingsModal] = useState(false);

  // Tone player state
  const [selectedAudioLink, setSelectedAudioLink] = useState(null);
  const [showURLBasedAudioPlayer, setShowURLBasedAudioPlayer] = useState(false);

  useEffect(() => {
    fetchServices();
    fetchFolders();
  }, []);

  useEffect(() => {
    fetchLectionaryEventsForYear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Fetch once on mount

  // Handle URL parameter changes for folder navigation
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const folderId = params.get("resource_folder");

    if (folderId && folders.length > 0) {
      const folder = folders.find((f) => f.id.toString() === folderId);
      if (
        folder &&
        (!selectedFolder || selectedFolder.id.toString() !== folderId)
      ) {
        setSelectedFolder(folder);
        fetchResourcesByFolder(folderId);
      }
    } else if (!folderId && selectedFolder) {
      // URL doesn't have folder parameter, clear selected folder
      setSelectedFolder(null);
      setResources([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, folders]);

  useEffect(() => {
    filterEventsByMonth();
    // Clear selected date when month changes
    setSelectedDate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, lectionaryCache]);

  const fetchServices = async () => {
    try {
      const response = await api.get("/api/services");
      if (response.data && Array.isArray(response.data)) {
        const validServices = response.data
          .filter(
            (service) =>
              service &&
              service.name &&
              service.day &&
              service.time &&
              service.id
          )
          .map((service) => ({
            ...service,
            description: service.description || "",
            language: service.language || "",
            category: service.category || "regular",
          }));
        setServices(validServices);
        setRegularServices(
          validServices.filter((service) => service.category === "regular")
        );
        setSpecialServices(
          validServices.filter((service) => service.category === "special")
        );
      } else {
        setServices([]);
        setRegularServices([]);
        setSpecialServices([]);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      setServices([]);
      setRegularServices([]);
      setSpecialServices([]);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await api.get("/api/service-resources/folders");
      if (response.data && Array.isArray(response.data)) {
        setFolders(response.data);
      } else {
        setFolders([]);
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  };

  const fetchResourcesByFolder = async (folderId) => {
    try {
      setResourcesLoading(true);
      const response = await api.get(
        `/api/service-resources/folders/${folderId}/resources`
      );
      if (response.data && Array.isArray(response.data)) {
        setResources(response.data);
      } else {
        setResources([]);
      }
    } catch (error) {
      console.error("Error fetching resources:", error);
      setResources([]);
    } finally {
      setResourcesLoading(false);
    }
  };

  const fetchLectionaryEventsForYear = async () => {
    try {
      // Check if we already have this year cached
      if (lectionaryCache[currentYear]) {
        filterEventsByMonth();
        return;
      }

      setLectionaryLoading(true);
      // Fetch entire year
      const response = await api.get(`/api/lectionary?year=${currentYear}`);
      if (response.data && response.data.events) {
        // Cache the entire year's data
        setLectionaryCache((prev) => ({
          ...prev,
          [currentYear]: response.data.events,
        }));
      } else {
        setLectionaryCache((prev) => ({
          ...prev,
          [currentYear]: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching lectionary events:", error);
      setLectionaryCache((prev) => ({
        ...prev,
        [currentYear]: [],
      }));
    } finally {
      setLectionaryLoading(false);
    }
  };

  const filterEventsByMonth = () => {
    const yearData = lectionaryCache[currentYear] || [];

    // Filter events for current month using string manipulation to avoid timezone issues
    const monthEvents = yearData.filter((event) => {
      // Parse date components directly from YYYY-MM-DD format
      const [year, month] = event.date.split("-").map(Number);
      return (
        month - 1 === currentMonth && // month is 1-indexed in date string, 0-indexed in JS
        year === currentYear
      );
    });

    setLectionaryEvents(monthEvents);
  };

  // Resource utility functions
  // eslint-disable-next-line no-unused-vars
  const getYouTubeVideoId = (url) => {
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
  };

  // eslint-disable-next-line no-unused-vars
  const isYouTubeUrl = (url) => {
    return url && (url.includes("youtube.com") || url.includes("youtu.be"));
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case "Audio":
        return <Music className="w-6 h-6" />;
      case "Video":
        return <Video className="w-6 h-6" />;
      case "Documents":
        return <FileText className="w-6 h-6" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  };

  const filteredResources =
    activeResourceFilter === "All"
      ? resources
      : resources.filter(
          (resource) => resource.resource_type === activeResourceFilter
        );

  const handleFolderClick = async (folder) => {
    // Update URL with folder parameter
    navigate(`${location.pathname}?resource_folder=${folder.id}`, {
      replace: false,
    });
    setActiveResourceFilter("All");
  };

  const handleBackToFolders = () => {
    // Remove folder parameter from URL
    navigate(location.pathname, { replace: false });
    setActiveResourceFilter("All");
  };

  const openResourceModal = (resource) => {
    setSelectedResource(resource);
    setShowResourceModal(true);
  };

  const closeResourceModal = () => {
    setSelectedResource(null);
    setShowResourceModal(false);
  };

  // Lectionary functions
  const handleDateSelect = (date) => {
    setSelectedDate(date === selectedDate ? null : date);
  };

  const handleEventClick = (event) => {
    if (event.hasReadings) {
      setSelectedEvent(event);
      setShowReadingsModal(true);
    }
  };

  const closeReadingsModal = () => {
    setSelectedEvent(null);
    setShowReadingsModal(false);
  };

  const handleToneClick = (toneInfo) => {
    setSelectedAudioLink(toneInfo);
    setShowURLBasedAudioPlayer(true);
  };

  const closeURLBasedAudioPlayer = () => {
    setSelectedAudioLink(null);
    setShowURLBasedAudioPlayer(false);
  };

  const getFilteredEvents = () => {
    if (!selectedDate) {
      return lectionaryEvents;
    }
    return lectionaryEvents.filter((event) => event.date === selectedDate);
  };

  const renderResourceModal = () => {
    if (!selectedResource) return null;

    const { resource_type, resource_name } = selectedResource;

    // For Documents, use MediaViewer for images and documents
    if (resource_type === "Documents") {
      return (
        <MediaViewer
          isOpen={showResourceModal}
          onClose={closeResourceModal}
          resource={selectedResource}
          title={resource_name}
        />
      );
    }

    // For Audio, use AudioViewer
    if (resource_type === "Audio") {
      return (
        <AudioViewer
          isOpen={showResourceModal}
          onClose={closeResourceModal}
          resource={selectedResource}
          title={resource_name}
        />
      );
    }

    // For Video, use VideoViewer
    if (resource_type === "Video") {
      return (
        <VideoViewer
          isOpen={showResourceModal}
          onClose={closeResourceModal}
          resource={selectedResource}
          title={resource_name}
        />
      );
    }

    return null;
  };

  const renderServiceCard = (service) => (
    <div
      key={service.id}
      className={`${
        service.category === "special" ? "bg-amber-50" : "bg-white"
      } rounded-lg shadow-lg p-8`}
    >
      <h3 className="text-2xl font-semibold text-gray-900 mb-4">
        {service.name}
      </h3>

      <div className="space-y-3 mb-6">
        <div className="flex items-center">
          <Calendar className="w-5 h-5 text-red-600 mr-3" />
          <span className="text-gray-700">{service.day}</span>
        </div>

        <div className="flex items-center">
          <Clock className="w-5 h-5 text-red-600 mr-3" />
          <span className="text-gray-700">{service.time}</span>
        </div>

        {service.language && (
          <div className="flex items-center">
            <BookOpen className="w-5 h-5 text-red-600 mr-3" />
            <span className="text-gray-700">{service.language}</span>
          </div>
        )}
      </div>

      {service.description && (
        <p className="text-gray-600 leading-relaxed whitespace-pre-line">
          <Linkify options={linkifyOptions}>{service.description}</Linkify>
        </p>
      )}
    </div>
  );

  const linkifyOptions = {
    attributes: {
      class: "text-red-600 hover:text-red-800 underline break-all",
      target: "_blank",
      rel: "noopener noreferrer",
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-r from-red-800 to-red-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Worship Services</h1>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed">
            Join us in worship as we celebrate the ancient liturgical traditions
            of the Malankara Orthodox Church.
          </p>
        </div>
      </section>
      {!selectedFolder && (
        <>
          {/* Regular Services Section */}
          <section id="sunday-service" className="py-20 bg-amber-50">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Sunday Service
                </h2>
                <div className="w-24 h-1 bg-red-600 mx-auto mb-4"></div>
              </div>

              {initialLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Loading services...</p>
                </div>
              ) : (
                <>
                  {services.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        No Services Available
                      </h3>
                      <p className="text-gray-500 mb-6">
                        Service schedules will appear here once they are added.
                      </p>
                    </div>
                  ) : (
                    <>
                      {regularServices.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          {regularServices
                            .filter((service) => service && service.id)
                            .map(renderServiceCard)}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-gray-600 mb-2">
                            No Sunday Services
                          </h3>
                          <p className="text-gray-500">
                            Sunday service schedules will appear here once
                            added.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Special Day Section */}
          <section id="special-day" className="py-20">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Special Day
                </h2>
                <div className="w-24 h-1 bg-red-600 mx-auto"></div>
              </div>

              {initialLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">
                    Loading special services...
                  </p>
                </div>
              ) : specialServices.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {specialServices
                    .filter((service) => service && service.id)
                    .map(renderServiceCard)}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No Special Day Services Available
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Special occasion service schedules will appear here once
                    they are added.
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Lectionary Section */}
          <section id="lectionary" className="py-20 bg-amber-50">
            <div className="max-w-7xl mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Lectionary
                </h2>
                <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
                <p className="text-xl max-w-4xl mx-auto leading-relaxed text-gray-600">
                  View the daily scripture readings and feast days of the Indian
                  Orthodox Church.
                </p>
              </div>

              {lectionaryLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Loading lectionary...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left side - Calendar */}
                  <div>
                    <LectionaryCalendar
                      selectedDate={selectedDate}
                      onDateSelect={handleDateSelect}
                      currentMonth={currentMonth}
                      currentYear={currentYear}
                      onMonthChange={setCurrentMonth}
                    />
                  </div>

                  {/* Right side - Events Table */}
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="bg-gradient-to-r from-red-800 to-red-600 text-white px-6 py-4">
                      <h3 className="text-xl font-bold">
                        {selectedDate
                          ? `Events on ${(() => {
                              const [year, month, day] = selectedDate
                                .split("-")
                                .map(Number);
                              return new Date(
                                Date.UTC(year, month - 1, day)
                              ).toLocaleDateString("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                                timeZone: "UTC",
                              });
                            })()}`
                          : `All Events in ${new Date(
                              Date.UTC(currentYear, currentMonth, 1)
                            ).toLocaleDateString("en-US", {
                              month: "long",
                              timeZone: "UTC"
                            })}`}
                      </h3>
                    </div>

                    <div className="overflow-y-auto max-h-[480px]">
                      {getFilteredEvents().length > 0 ? (
                        <table className="w-full">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Event
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Tone
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {getFilteredEvents().map((event) => {
                              // Pass all year's events for reset point detection
                              const yearEvents = lectionaryCache[currentYear] || [];
                              const toneInfo = getToneInfo(event.date, event.summary, yearEvents);
                              return (
                                <tr
                                  key={event.id}
                                  className={`hover:bg-gray-50 transition-colors ${
                                    event.hasReadings ? "cursor-pointer" : ""
                                  }`}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {(() => {
                                      const [year, month, day] = event.date
                                        .split("-")
                                        .map(Number);
                                      return new Date(
                                        Date.UTC(year, month - 1, day)
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        timeZone: "UTC",
                                      });
                                    })()}
                                  </td>
                                  <td
                                    className="px-6 py-4 text-sm cursor-pointer"
                                    onClick={() => handleEventClick(event)}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span
                                        className={`${
                                          event.hasReadings
                                            ? "text-red-600 font-medium hover:underline"
                                            : "text-gray-900"
                                        }`}
                                      >
                                        {event.summary}
                                      </span>
                                      {event.hasReadings && (
                                        <BookOpen className="w-4 h-4 text-red-600 ml-2 flex-shrink-0" />
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {toneInfo ? (
                                      <div className="flex flex-col gap-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleToneClick(toneInfo);
                                          }}
                                          className={`inline-flex items-center gap-1 hover:underline transition-colors ${
                                            toneInfo.isFeastDay
                                              ? "text-amber-600 hover:text-amber-800 font-semibold"
                                              : "text-blue-600 hover:text-blue-800"
                                          }`}
                                          title={toneInfo.isFeastDay ? `Feast Day: ${toneInfo.feastName}` : undefined}
                                        >
                                          <Music className="w-4 h-4" />
                                          <span>Tone {toneInfo.tone}</span>
                                          {toneInfo.isFeastDay && (
                                            <span className="text-xs">⭐</span>
                                          )}
                                        </button>
                                        {toneInfo.isFeastDay && (
                                          <span className="text-xs text-amber-700 italic">
                                            Feast Day
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : (
                        <div className="text-center py-12">
                          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">
                            {selectedDate
                              ? "No events on this date"
                              : "No events this month"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Resources Section */}
      <section
        id="resources"
        className={`py-20 ${selectedFolder ? "bg-amber-50" : "bg-white"}`}
      >
        <div className="max-w-6xl mx-auto px-4">
          {selectedFolder ? (
            // Inside a folder - show folder header
            <>
              {/* Resource Filter - Only shown inside folder */}
              <div className="flex flex-wrap justify-center mb-12 gap-4">
                {["All", "Audio", "Video", "Documents"].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setActiveResourceFilter(filter)}
                    className={`px-6 py-2 rounded-full font-medium transition-colors ${
                      activeResourceFilter === filter
                        ? "bg-red-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
              {/* Back Button */}
              <div className="flex items-center text-sm text-gray-600 mb-[1.5rem]">
                <button
                  onClick={handleBackToFolders}
                  className="flex items-center hover:text-red-600 transition-colors"
                >
                  <Home className="w-4 h-4 mr-1" />
                  Service Resources
                </button>
                <ChevronRight className="w-4 h-4 mx-1" />
                <span className="font-medium text-red-600">
                  {selectedFolder.folder_name}
                </span>
              </div>

              {/* Resources Grid */}
              {resourcesLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Loading resources...</p>
                </div>
              ) : filteredResources.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredResources.map((resource) => (
                    <div
                      key={resource.id}
                      className="group bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
                      onClick={() => openResourceModal(resource)}
                    >
                      <div className="p-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mb-4 text-red-600">
                          {getResourceIcon(resource.resource_type)}
                        </div>
                        <h3
                          className="text-lg font-semibold text-gray-900 mb-2"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {resource.resource_name}
                        </h3>
                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {resource.resource_type}
                          </span>
                          {resource.resource_type === "Documents" ? (
                            <BookOpen className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors" />
                          ) : (
                            <Play className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No Resources Found
                  </h3>
                  <p className="text-gray-500">
                    {activeResourceFilter === "All"
                      ? "No resources in this folder yet."
                      : `No ${activeResourceFilter.toLowerCase()} resources in this folder yet.`}
                  </p>
                </div>
              )}
            </>
          ) : (
            // Folders Grid - show folders when no folder is selected
            <>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Resources
                </h2>
                <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
                <p className="text-xl max-w-4xl mx-auto leading-relaxed text-gray-600">
                  Access our collection of spiritual resources including audio
                  recordings, videos, and documents.
                </p>
              </div>

              {foldersLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Loading folders...</p>
                </div>
              ) : folders.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {folders.map((folder) => (
                    <div
                      key={folder.id}
                      className="group bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
                      onClick={() => handleFolderClick(folder)}
                    >
                      <div className="p-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-amber-100 rounded-lg mb-4 text-amber-600">
                          <Folder className="w-6 h-6" />
                        </div>
                        <h3
                          className="text-lg font-semibold text-gray-900 mb-2"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {folder.folder_name}
                        </h3>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            {folder.itemCount || folder.item_count || 0}{" "}
                            {(folder.itemCount || folder.item_count || 0) === 1
                              ? "item"
                              : "items"}
                          </span>
                          <ArrowLeft className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors rotate-180" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Folder className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No Folders Found
                  </h3>
                  <p className="text-gray-500">
                    Resource folders will appear here once they are created.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Resource Modal */}
      {showResourceModal && renderResourceModal()}

      {/* Readings Modal */}
      <ReadingsModal
        isOpen={showReadingsModal}
        onClose={closeReadingsModal}
        event={selectedEvent}
      />

      {/* Tone Player Modal */}
      <URLBasedAudioPlayer
        isOpen={showURLBasedAudioPlayer}
        onClose={closeURLBasedAudioPlayer}
        audioInfo={selectedAudioLink}
      />
    </div>
  );
};

export default Services;
